import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, Users, Video, Shield, Target, Sparkles, Award, ArrowRight } from 'lucide-react';
import SEO, { SEO_CONFIG } from '@/components/SEO';

const AboutPage = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <SEO {...SEO_CONFIG.about} />
      
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            {isEnglish ? 'About Pilatika' : 'Pilatika Hakkında'}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            {isEnglish 
              ? 'Turkey\'s leading online wellness platform combining AI technology with expert instruction'
              : 'Yapay zeka teknolojisi ile uzman eğitmenliği birleştiren Türkiye\'nin lider online wellness platformu'}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Mission Section */}
        <section className="mb-12">
          <Card className="overflow-hidden">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Target className="h-7 w-7 text-purple-600" />
                {isEnglish ? 'Our Mission' : 'Misyonumuz'}
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  {isEnglish 
                    ? 'Pilatika is a digital sports and wellness education platform that makes healthy living accessible to everyone. We combine cutting-edge AI technology with expert human instruction to create personalized wellness journeys.'
                    : 'Pilatika, sağlıklı yaşamı herkes için erişilebilir kılan dijital spor ve wellness eğitim platformudur. En son yapay zeka teknolojisini uzman eğitmenlikle birleştirerek kişiselleştirilmiş wellness yolculukları oluşturuyoruz.'}
                </p>
                
                <p>
                  {isEnglish
                    ? 'Through our platform, you can access digital course content and live classes prepared by certified instructors in pilates, yoga, breathing exercises, stretching and similar wellness disciplines. Our AI Coach and AI Dietitian provide 24/7 personalized guidance to support your goals.'
                    : 'Platformumuz üzerinden pilates, yoga, nefes egzersizleri, esneme ve benzeri wellness disiplinlerinde sertifikalı eğitmenler tarafından hazırlanan dijital ders içeriklerine ve canlı derslere erişebilirsiniz. AI Koçumuz ve AI Diyetisyenimiz, hedeflerinizi desteklemek için 7/24 kişiselleştirilmiş rehberlik sunar.'}
                </p>
                
                <p>
                  {isEnglish
                    ? 'Users can purchase individual lessons, live classes, or subscription packages to access content online. All services are provided digitally - we focus entirely on online wellness education with no physical products.'
                    : 'Kullanıcılar, içeriklere online olarak erişmek için tekil ders, canlı ders veya abonelik paketleri satın alabilir. Tüm hizmetlerimiz dijital ortamda sunulmaktadır - fiziksel ürün satışı olmadan tamamen online wellness eğitimine odaklanıyoruz.'}
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Values Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {isEnglish ? 'What Makes Us Different' : 'Bizi Farklı Kılan'}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Sparkles className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      {isEnglish ? 'AI-Powered Coaching' : 'Yapay Zeka Destekli Koçluk'}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {isEnglish 
                        ? 'Our AI Coach provides personalized motivation, goal tracking, and wellness recommendations available 24/7.'
                        : 'AI Koçumuz, 7/24 erişilebilir kişiselleştirilmiş motivasyon, hedef takibi ve wellness önerileri sunar.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      {isEnglish ? 'Expert Instructors' : 'Uzman Eğitmenler'}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {isEnglish 
                        ? 'All our instructors are certified professionals with proven expertise in their wellness disciplines.'
                        : 'Tüm eğitmenlerimiz, wellness disiplinlerinde kanıtlanmış uzmanlığa sahip sertifikalı profesyonellerdir.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Video className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      {isEnglish ? 'Flexible Learning' : 'Esnek Öğrenme'}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {isEnglish 
                        ? 'Access video lessons and live classes anytime, anywhere. Learn at your own pace with on-demand content.'
                        : 'Video derslere ve canlı derslere istediğiniz zaman, istediğiniz yerden erişin. İsteğe bağlı içeriklerle kendi hızınızda öğrenin.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <Shield className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-2">
                      {isEnglish ? 'Secure & Trusted' : 'Güvenli & Güvenilir'}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {isEnglish 
                        ? 'All payments are secured with iyzico. Your data privacy is protected under KVKK regulations.'
                        : 'Tüm ödemeler iyzico güvencesiyle alınır. Veri gizliliğiniz KVKK düzenlemeleri kapsamında korunmaktadır.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Stats Section */}
        <section className="mb-12 bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-8">
            {isEnglish ? 'Pilatika in Numbers' : 'Rakamlarla Pilatika'}
          </h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-4xl font-bold mb-1">AI</div>
              <div className="text-white/80 text-sm">{isEnglish ? 'Powered Coach' : 'Destekli Koç'}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-1">7/24</div>
              <div className="text-white/80 text-sm">{isEnglish ? 'Available' : 'Erişim'}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-1">100%</div>
              <div className="text-white/80 text-sm">{isEnglish ? 'Digital' : 'Dijital'}</div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {isEnglish ? 'Ready to Start Your Journey?' : 'Yolculuğunuza Başlamaya Hazır mısınız?'}
          </h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            {isEnglish 
              ? 'Join thousands of users who have transformed their wellness routine with Pilatika.'
              : 'Pilatika ile wellness rutinlerini dönüştüren binlerce kullanıcıya katılın.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
                {isEnglish ? 'Get Started Free' : 'Ücretsiz Başla'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/ai-coach">
              <Button size="lg" variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50">
                <Sparkles className="mr-2 h-4 w-4" />
                {isEnglish ? 'Try AI Coach' : 'AI Koç\'u Dene'}
              </Button>
            </Link>
          </div>
        </section>

      </div>
    </div>
  );
};

export default AboutPage;
